import { type RouteObject } from 'react-router';

import { routeDefinitions, RouteId } from '@trello/router/routes';
import {
  getMemberAccountUrl,
  getMemberActivityUrl,
  getMemberCardsUrl,
  getMemberProfileUrl,
} from '@trello/urls';
import { importWithRetry } from '@trello/use-lazy-component';

import {
  createLoggedInGuard,
  createUsernameAuthGuard,
  createUsernameRedirect,
  usernameContextProvider,
} from './middleware';
import { loadMiddlewareIfGateEnabled } from './migration';

/** This is only needed as a temporary workaround while https://github.com/remix-run/react-router/issues/14571 is being addressed  */
const NOOP_LOADER = async () => null;

/**
 * Member-related route configurations
 */
export const memberRoutes: RouteObject[] = [
  // u/:username/cards/*
  {
    path: routeDefinitions[RouteId.MEMBER_CARDS].pattern,
    middleware: [
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        usernameContextProvider,
      ),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        createUsernameRedirect({
          createRedirectUrl: getMemberCardsUrl,
          getUsernameFromParams: (params) => params.username,
        }),
      ),
    ],
    lazy: async () => {
      const { Component } = await importWithRetry(
        () =>
          import(
            /* webpackChunkName: "member-cards-route" */ './MemberCardsRoute'
          ),
      );
      return {
        Component,
      };
    },
    loader: NOOP_LOADER,
  },
  // u/:username/activity
  {
    path: routeDefinitions[RouteId.MEMBER_ACTIVITY].pattern,
    middleware: [
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        usernameContextProvider,
      ),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        createUsernameRedirect({
          createRedirectUrl: getMemberActivityUrl,
          getUsernameFromParams: (params) => params.username,
        }),
      ),
    ],
    lazy: async () => {
      const { Component } = await importWithRetry(
        () =>
          import(
            /* webpackChunkName: "member-activity-route" */ './MemberActivityRoute'
          ),
      );
      return {
        Component,
      };
    },
    loader: NOOP_LOADER,
  },
  // u/:username/account
  {
    path: routeDefinitions[RouteId.MEMBER_ACCOUNT].pattern,
    middleware: [
      createLoggedInGuard((params) => getMemberActivityUrl(params.username)),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        usernameContextProvider,
      ),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        createUsernameRedirect({
          createRedirectUrl: getMemberAccountUrl,
          getUsernameFromParams: (params) => params.username,
        }),
      ),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        createUsernameAuthGuard({
          createRedirectUrl: getMemberActivityUrl,
          getUsernameFromParams: (params) => params.username,
        }),
      ),
    ],
    lazy: async () => {
      const { Component } = await importWithRetry(
        () =>
          import(
            /* webpackChunkName: "member-account-route" */ './MemberAccountRoute'
          ),
      );
      return {
        Component,
      };
    },
    loader: NOOP_LOADER,
  },
  // u/:username/ai-settings
  {
    path: routeDefinitions[RouteId.MEMBER_AI_SETTINGS].pattern,
    middleware: [createLoggedInGuard()],
    lazy: async () => {
      const { Component } = await importWithRetry(
        () =>
          import(
            /* webpackChunkName: "member-ai-settings-route" */ './MemberAiSettingsRoute'
          ),
      );
      return {
        Component,
      };
    },
    loader: NOOP_LOADER,
  },
  // me/inbox
  {
    path: routeDefinitions[RouteId.INBOX].pattern,
    middleware: [createLoggedInGuard()],
    lazy: async () => {
      const { Component, loader } = await importWithRetry(
        () => import(/* webpackChunkName: "inbox-route" */ './InboxRoute'),
      );
      return {
        Component,
        loader,
      };
    },
  },
  // u/:username/*
  {
    path: routeDefinitions[RouteId.MEMBER_PROFILE_SECTION].pattern,
    middleware: [
      createLoggedInGuard((params) => getMemberActivityUrl(params.username)),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        usernameContextProvider,
      ),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        createUsernameRedirect({
          createRedirectUrl: getMemberProfileUrl,
          getUsernameFromParams: (params) => params.username,
        }),
      ),
      loadMiddlewareIfGateEnabled(
        'tplat_migrate_middleware_member',
        createUsernameAuthGuard({
          createRedirectUrl: getMemberActivityUrl,
          getUsernameFromParams: (params) => params.username,
        }),
      ),
    ],
    lazy: async () => {
      const { Component } = await importWithRetry(
        () =>
          import(
            /* webpackChunkName: "member-profile-route" */ './MemberProfileRoute'
          ),
      );
      return {
        Component,
      };
    },
    loader: NOOP_LOADER,
  },
];
